import {useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {formatSize} from '@/hooks/sgsst/evidencias/use-evidence'
import {translateApiError} from '@/lib/errors'

// Mirrors the backend `ALLOWED_HALLAZGO_FOTO_CONTENT_TYPES`.
const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp'
const PHOTO_ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp'])
const PHOTO_MAX_BYTES = 20 * 1024 * 1024

export function PhotoUploadField({
  testId,
  // Foto rows share one flat id across instances: only one row is interactive at a time.
  inputTestId,
  buttonTestId,
  buttonLabel,
  onUpload,
  onUploaded,
  listAttached = true,
}: {
  testId: string
  inputTestId?: string
  buttonTestId?: string
  buttonLabel: string
  onUpload: (file: File, onProgress: (pct: number) => void) => Promise<unknown>
  onUploaded?: () => void
  /** False when the caller renders its own thumbnails from server data. */
  listAttached?: boolean
}) {
  const {t} = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const [progress, setProgress] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [attached, setAttached] = useState<{filename: string; size_bytes: number}[]>([])

  async function start(files: FileList | null) {
    const file = files?.[0]
    if (!file) return
    setError(null)
    if (!PHOTO_ALLOWED_TYPES.has(file.type)) {
      setError(t('entrevista.foto.badFormat'))
      return
    }
    if (file.size > PHOTO_MAX_BYTES) {
      setError(t('entrevista.foto.tooBig'))
      return
    }
    try {
      setProgress(0)
      await onUpload(file, setProgress)
      setAttached((prev) => [...prev, {filename: file.name, size_bytes: file.size}])
      setProgress(null)
      onUploaded?.()
    } catch (err) {
      setProgress(null)
      setError(translateApiError(err))
    }
    if (inputRef.current) inputRef.current.value = ''
  }

  return (
    <div className="space-y-1.5" data-testid={testId}>
      <input
        ref={inputRef}
        type="file"
        accept={PHOTO_ACCEPT}
        hidden
        data-testid={inputTestId ?? `${testId}-input`}
        onChange={(e) => start(e.target.files)}
      />
      {progress !== null ? (
        <div className="h-1.5 w-32 overflow-hidden rounded-full bg-secondary">
          <div className="h-full rounded-full bg-primary transition-all" style={{width: `${progress}%`}} />
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid={buttonTestId ?? `${testId}-upload`}
          onClick={() => inputRef.current?.click()}>
          {buttonLabel}
        </Button>
      )}
      {listAttached && attached.length > 0 && (
        <ul className="space-y-0.5" data-testid={`${buttonTestId ?? testId}-attached-list`}>
          {attached.map((item, i) => (
            <li key={`${item.filename}-${i}`} className="font-mono text-[11px] text-muted-foreground">
              {item.filename} · {formatSize(item.size_bytes)}
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p className="text-xs text-destructive" data-testid={`${buttonTestId ?? testId}-error`}>
          {error}
        </p>
      )}
    </div>
  )
}
